import { useState } from "react";
import { Copy, Check, Code2, Zap, Shield, Eye } from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

export default function WidgetEmbed() {
    const [state, setState] = useState("California");
    const [topic, setTopic] = useState("");
    const [accent, setAccent] = useState("#FF3B30");
    const [copied, setCopied] = useState(false);

    const origin =
        typeof window !== "undefined" ? window.location.origin : "https://higherpowermovement.com";
    const snippet = `<script src="${origin}/api/widget.js"\n        data-hpm-state="${state}"\n        data-hpm-topic="${topic}"\n        data-hpm-accent="${accent}"></script>`;

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(snippet);
            setCopied(true);
            toast.success("Copied — paste it on your site.");
            setTimeout(() => setCopied(false), 1800);
        } catch {
            toast.error("Couldn't copy. Long-press the code to copy manually.");
        }
    };

    return (
        <div className="bg-[#0a0a0a] min-h-screen text-white">
            <Header />

            {/* HERO */}
            <section className="border-b-2 border-white/20">
                <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-16">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFCC00] mb-4">
                        FREE · ONE LINE · ZERO CODE
                    </div>
                    <h1 className="font-anton uppercase leading-[0.9] text-5xl sm:text-7xl">
                        Put a <span className="text-[#FF3B30]">free legal-help button</span><br />
                        on any website.
                    </h1>
                    <p className="mt-6 max-w-2xl font-sans text-white/75 text-base sm:text-lg leading-relaxed">
                        Drop one script tag into your site, your client portal, your CBO landing
                        page, your church website — and every visitor gets a one-click "FREE
                        LEGAL HELP" button that opens HPM's lead-capture form. We do the rest.
                    </p>
                </div>
            </section>

            {/* HOW IT WORKS */}
            <section className="border-b-2 border-white/20">
                <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-12 grid grid-cols-1 md:grid-cols-3 gap-0 border-2 border-white/20">
                    {[
                        { icon: Code2, label: "01 · DROP IN", body: "Paste one script tag in your site's HTML. Anywhere on the page." },
                        { icon: Eye, label: "02 · APPEARS", body: "A floating 'FREE LEGAL HELP' button shows on every page. Mobile + desktop." },
                        { icon: Zap, label: "03 · CAPTURE", body: "Visitor taps it. A modal opens. Their info routes straight into HPM's marketplace." },
                    ].map((s, i) => {
                        const I = s.icon;
                        return (
                            <div
                                key={i}
                                className="p-6 [&:not(:last-child)]:md:border-r-2 [&:not(:last-child)]:md:border-b-0 [&:not(:last-child)]:border-b-2 border-white/20"
                                data-testid={`widget-step-${i}`}
                            >
                                <I size={24} className="text-[#FF3B30]" />
                                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mt-3">
                                    {s.label}
                                </div>
                                <div className="font-sans text-white/85 text-sm mt-2 leading-relaxed">{s.body}</div>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* CONFIG + SNIPPET */}
            <section className="border-b-2 border-white/20">
                <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-12">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF3B30] mb-3">
                        YOUR EMBED CODE
                    </div>
                    <h2 className="font-anton text-4xl sm:text-5xl uppercase mb-6">
                        Configure and copy.
                    </h2>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                        <div>
                            <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-1 block">
                                DEFAULT STATE (PRE-FILLS FORM)
                            </label>
                            <input
                                value={state}
                                onChange={(e) => setState(e.target.value)}
                                className="bg-transparent border-2 border-white/30 px-3 py-2 font-mono text-sm text-white w-full focus:border-white outline-none"
                                data-testid="widget-state"
                            />
                        </div>
                        <div>
                            <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-1 block">
                                TOPIC KEY (OPTIONAL · E.G. KYR_TRAFFIC_STOP)
                            </label>
                            <input
                                value={topic}
                                onChange={(e) => setTopic(e.target.value)}
                                className="bg-transparent border-2 border-white/30 px-3 py-2 font-mono text-sm text-white w-full focus:border-white outline-none"
                                data-testid="widget-topic"
                            />
                        </div>
                        <div>
                            <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-1 block">
                                ACCENT COLOR
                            </label>
                            <input
                                value={accent}
                                onChange={(e) => setAccent(e.target.value)}
                                className="bg-transparent border-2 border-white/30 px-3 py-2 font-mono text-sm text-white w-full focus:border-white outline-none"
                                data-testid="widget-accent"
                            />
                        </div>
                    </div>

                    <div className="mt-6 relative">
                        <pre
                            className="bg-[#000] border-2 border-white/20 p-5 font-mono text-xs sm:text-sm text-[#FFCC00] overflow-auto whitespace-pre-wrap break-words"
                            data-testid="widget-snippet"
                        >
                            {snippet}
                        </pre>
                        <button
                            onClick={copy}
                            className="absolute top-3 right-3 btn-brutal text-[10px]"
                            data-testid="widget-copy"
                        >
                            {copied ? <Check size={12} /> : <Copy size={12} />} {copied ? "COPIED" : "COPY"}
                        </button>
                    </div>

                    <div className="mt-4 font-mono text-[10px] uppercase tracking-[0.25em] text-white/50 flex items-start gap-2">
                        <Shield size={11} className="mt-[1px] flex-shrink-0" />
                        <span>
                            FREE FOREVER FOR NON-PROFITS, CBOs, CHURCHES, COMMUNITY ORGS. NO API KEY,
                            NO BILLING, NO CATCH. EVERY LEAD CAPTURED GOES STRAIGHT TO HPM'S VETTED
                            FIRM MARKETPLACE — YOU JUST GAVE A NEIGHBOR FREE LEGAL HELP.
                        </span>
                    </div>
                </div>
            </section>

            {/* USE CASES */}
            <section>
                <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-12">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFCC00] mb-3">
                        WHO'S EMBEDDING THIS
                    </div>
                    <h2 className="font-anton text-4xl uppercase mb-6">
                        Built for the network that levels the system.
                    </h2>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-3xl font-sans text-white/85 text-base">
                        {[
                            "Tenant unions & housing organisers",
                            "Bail funds & jail-support hotlines",
                            "Immigrant-rights coalitions",
                            "Churches & mosques with legal-aid ministries",
                            "Public defenders' alumni networks",
                            "Civil-rights nonprofits & ACLU affiliates",
                            "Mutual-aid pages & community bulletins",
                            "Re-entry & expungement programs",
                        ].map((u, i) => (
                            <li key={i} className="flex gap-3 items-start">
                                <span className="font-anton text-xl text-[#FF3B30]">↳</span>
                                <span>{u}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            <Footer disclaimer="" />
        </div>
    );
}
